import { Component, OnInit, ViewChild } from '@angular/core';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { EmployeeUserControllerRequestService } from './service/employee-user-controller-request.service';
import { BranchChoicesService } from '../../../services/branch/branch-choices.service';
import {
  EditEmployeeUserHelper,
  EmployeeTypeOption,
} from './helper/edit-employee-user-helper';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { User } from 'src/app/model/user';
import Branch from 'src/app/model/branch/branch';
import { OfflineProfilePicture } from './helper/offline-profile-picture';
import { ModalService } from 'src/app/services/modal.service';
import { RequestService } from 'src/app/services/request.service';
import { SnackService } from 'src/app/services/snack.service';
import { FormatPhoneNumber } from 'src/app/modules/app-pipes/format-phonenumber';
import { NgModel } from '@angular/forms';
import UserHelper from 'src/app/helpers/user-helper';
import { Role } from 'src/app/model/role';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import { NgForage } from 'ngforage';

@Component({
  selector: 'edit-employee-user',
  templateUrl: './edit-employee-user.component.html',
  styleUrls: ['./edit-employee-user.component.scss'],
  providers: [FormatPhoneNumber],
})
export class EditEmployeeUserComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  user: User;
  offlineProfilePicture: OfflineProfilePicture;

  branchChoices: Branch[];
  employeeTypeOptions: EmployeeTypeOption[];

  adminChecked: boolean = false;

  init: boolean = false;
  saving: boolean = false;

  @ViewChild('emailRef')
  emailModel: NgModel;

  constructor(
    private employeeUserControllerRequestService: EmployeeUserControllerRequestService,
    private branchChoicesService: BranchChoicesService,
    private requestService: RequestService,
    private modalService: ModalService,
    private snackService: SnackService,
    private route: ActivatedRoute,
    private router: Router,
    private ngForage: NgForage,
    private formatPhoneNumber: FormatPhoneNumber
  ) {
    super();
  }

  ngOnInit(): void {
    this.branchChoicesService.updateBranchChoices();

    this.addSubscription(this.route.params, (params: Params) => {
      const id: number = params['id'] != null ? Number(params['id']) : null;
      this.doInitialize(id);
    });
    this.addSubscription(
      this.branchChoicesService.getBranchChoicesSubscribable(),
      (branches: Branch[]) => {
        this.branchChoices = branches;
      }
    );
  }

  private async doInitialize(id: number | null): Promise<void> {
    this.user =
      id == null || !GeneralHelper.isNumeric(id) || id === 0
        ? EditEmployeeUserHelper.initializeEmptyUser()
        : await this.getUser(id);
    this.adminChecked =
      this.user.roles != null && UserHelper.isAdmin(this.user);
    this.employeeTypeOptions = EditEmployeeUserHelper.getEmployeeTypeOptions(
      this.user
    );
    this.init = true;
  }

  private async getUser(id: number): Promise<User> {
    const user = await this.employeeUserControllerRequestService.employeeUser(
      id
    );
    EditEmployeeUserHelper.prepareUserForEdit(user);
    return user;
  }

  offlineProfilePictureChanged(
    offlineProfilePicture: OfflineProfilePicture
  ): void {
    this.offlineProfilePicture = offlineProfilePicture;
    this.snackService.showSuccess('Foto zal aangepast worden zodra u opslaagt');
  }

  phoneChanged(number: string): void {
    this.user.phone =
      number == null ? null : this.formatPhoneNumber.transform(number);
  }

  mobilePhoneNumberChanged(number: string): void {
    this.user.mobilePhoneNumber =
      number == null ? null : this.formatPhoneNumber.transform(number);
  }

  adminCheckedChanged(value: boolean): void {
    if (value) {
      if (this.user.roles == null) {
        this.user.roles = [];
      }
      if (!UserHelper.isAdmin(this.user)) {
        this.user.roles.push(new Role(<any>'ROLE_ADMIN')); // TODO refactor RoleName to a string enum
      }
    } else {
      CollectionHelper.filterInPlace(
        this.user.roles,
        (r) => r.name !== <any>'ROLE_ADMIN'
      );
    }
  }

  resendLoginEmail() {
    this.modalService.confirmDialog(
      'Login mail opnieuw versturen',
      'Bent u zeker dat u opnieuw een login mail wil versturen naar deze gebruiker?',
      (b) => {
        if (b) {
          this.addSubscription(
            this.requestService.post(
              {
                email: this.user.email,
                resend: true,
              },
              '/auth/recoverAccount'
            ),
            () => {
              this.snackService.showSuccess('Login mail opnieuw verstuurd');
            }
          );
        }
      }
    );
  }

  close(): void {
    this.router.navigate(['/users/employees']);
  }

  async submitUser(): Promise<void> {
    this.saving = true;

    const validationError = this.verifyUser();
    if (validationError != null) {
      this.snackService.showError(validationError);
      this.saving = false;
      return;
    }

    try {
      if (this.offlineProfilePicture != null) {
        await EditEmployeeUserHelper.prepareOfflineProfilePictureForSubmit(
          this.offlineProfilePicture,
          this.ngForage
        );
      }
      EditEmployeeUserHelper.prepareUserForSubmit(
        this.user,
        this.employeeTypeOptions
      );
      const requestBody = EditEmployeeUserHelper.getCreateOrUpdateBody(
        this.user,
        this.offlineProfilePicture
      );
      const submittedUser =
        this.user.id == null
          ? await this.employeeUserControllerRequestService.createEmployeeUser(
              requestBody
            )
          : await this.employeeUserControllerRequestService.editEmployeeUser(
              requestBody
            );
      EditEmployeeUserHelper.prepareUserForEdit(submittedUser);
      this.offlineProfilePicture = null;
      this.user = submittedUser;
      this.snackService.showSuccess('Gebruiker succesvol opgeslagen');
    } finally {
      this.saving = false;
    }
  }

  private verifyUser(): string {
    if (
      this.user.firstName == null ||
      StringHelper.isEmpty(this.user.firstName.toString())
    ) {
      return 'Gelieve een voornaam in te vullen';
    }
    if (
      this.user.lastName == null ||
      StringHelper.isEmpty(this.user.lastName.toString())
    ) {
      return 'Gelieve een achternaam in te vullen';
    }
    if (
      this.user.email == null ||
      StringHelper.isEmpty(this.user.email.toString())
    ) {
      return 'Gelieve een emailadres in te vullen';
    }
    if (!this.emailModel.valid) {
      return 'Gelieve een geldig e-mailadres in te vullen';
    }
    if (!this.employeeTypeOptions.some((t) => t.checked)) {
      return 'Gelieve minstens één werknemerrol aan te duiden';
    }

    return null;
  }

  compareEntities(o1: any, o2: any): boolean {
    return GeneralHelper.compareEntities(o1, o2);
  }
}
